import { useCallback, useEffect, useMemo, useState } from "react";
import gamesData from "../Component/gamesData";
import { api, unwrapCollection } from "../utils/api";

function favoriteGameIdsToNames(favoriteGameIds) {
  return favoriteGameIds
    .map((favoriteId) => {
      const match = Object.entries(gamesData).find(
        ([, details]) => details.id === favoriteId
      );
      return match?.[0] || null;
    })
    .filter(Boolean);
}

export function usePostsFeed({
  favoriteGameIds = [],
  currentUserId = "",
  filter4 = "",
} = {}) {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [friendPosts, setFriendPosts] = useState([]);

  const favoriteGameNames = useMemo(
    () => favoriteGameIdsToNames(favoriteGameIds),
    [favoriteGameIds]
  );

  const loadPosts = useCallback(async () => {
    try {
      setError(null);
      const result = await api.get("/posts?parentID=");
      const rawPosts = unwrapCollection(result);
      const formattedPosts = rawPosts.map((post) => ({
        ...post,
        title: post.attributes?.title,
        body: post.content || "",
        tags: post.attributes?.tags || [],
      }));
      setPosts(formattedPosts);
      setIsLoaded(true);
      return formattedPosts;
    } catch (nextError) {
      setError(nextError);
      setIsLoaded(true);
      return [];
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (filter4 !== "Friends" || !currentUserId) {
      setFriendPosts([]);
      return;
    }

    let cancelled = false;

    async function loadFriendPosts() {
      try {
        const connectionsResult = await api.get(
          `/connections?anyUserID=${currentUserId}`
        );
        const activeFriendIds = unwrapCollection(connectionsResult)
          .filter((connection) => connection.attributes?.status === "active")
          .map((connection) =>
            connection.fromUser.id === Number(currentUserId)
              ? connection.toUser.id
              : connection.fromUser.id
          );

        if (cancelled) return;

        setFriendPosts(
          posts.filter((post) => activeFriendIds.includes(Number(post.authorID)))
        );
      } catch (nextError) {
        if (!cancelled) {
          console.error("Error fetching friend's posts:", nextError);
          setFriendPosts([]);
        }
      }
    }

    loadFriendPosts();

    return () => {
      cancelled = true;
    };
  }, [currentUserId, filter4, posts]);

  return {
    error,
    favoriteGameNames,
    friendPosts,
    isLoaded,
    loadPosts,
    posts,
    setIsLoaded,
  };
}
